import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const packageOptions = [
    "Event/Offer Monthly Strategy ($400/mo)",
    "Loyalty Program Setup ($500)",
    "Monthly Subscription Box Setup ($500)",
    "Additional Consulting ($45/hr)",
    "Not sure yet",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        business_name: "",
        package_interest: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [newsletterEmail, setNewsletterEmail] = useState("");
    const [newsLoading, setNewsLoading] = useState(false);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submitContact = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email, and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            // Fire conversion events if analytics are loaded
            if (typeof window !== "undefined") {
                if (window.gtag) {
                    window.gtag("event", "generate_lead", {
                        event_category: "contact",
                        event_label: form.package_interest || "general",
                    });
                }
                if (window.fbq) {
                    window.fbq("track", "Lead");
                }
            }
            toast.success("Thanks — Amy will be in touch within 24 hours.");
            setForm({
                name: "",
                email: "",
                business_name: "",
                package_interest: "",
                message: "",
            });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
            if (process.env.NODE_ENV !== "production") {
                console.error(err);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const submitNewsletter = async (e) => {
        e.preventDefault();
        if (!newsletterEmail) return;
        setNewsLoading(true);
        try {
            await axios.post(`${API}/newsletter`, { email: newsletterEmail });
            if (typeof window !== "undefined") {
                if (window.gtag) {
                    window.gtag("event", "newsletter_signup", {
                        event_category: "engagement",
                    });
                }
                if (window.fbq) {
                    window.fbq("track", "Subscribe");
                }
            }
            toast.success("You're on the list.");
            setNewsletterEmail("");
        } catch {
            toast.error("Please use a valid email.");
        } finally {
            setNewsLoading(false);
        }
    };

    const inputClass =
        "w-full bg-transparent border-b border-brand-dark py-3 text-base text-brand-dark placeholder-brand-muted focus:outline-none focus:border-brand-orange transition-colors font-light";

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-32 bg-white border-t border-brand-border-light"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-10 md:gap-16">
                    {/* Left: heading + newsletter */}
                    <div className="col-span-12 lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <span className="h-px w-10 bg-brand-orange" />
                                <span className="text-xs uppercase tracking-[0.3em] text-brand-orange font-medium">
                                    Get in Touch
                                </span>
                            </div>
                            <h2
                                data-testid="contact-headline"
                                className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight text-brand-dark mb-8"
                            >
                                Let's pour
                                <br />
                                <span className="italic">something good.</span>
                            </h2>
                            <p className="text-brand-muted text-lg leading-relaxed max-w-md mb-10">
                                Tell me about your concept, your team, and what's keeping you up at
                                night. I'll reply within one business day.
                            </p>

                            <div className="space-y-4 mb-12">
                                <a
                                    href="mailto:hello@eatdrinkconsulting.com"
                                    className="flex items-center gap-3 text-brand-dark hover:text-brand-orange transition-colors"
                                    data-testid="contact-email-link"
                                >
                                    <Mail className="w-5 h-5" />
                                    <span className="font-serif italic text-xl">
                                        hello@eatdrinkconsulting.com
                                    </span>
                                </a>
                                <a
                                    href="tel:+13232125323"
                                    className="flex items-center gap-3 text-brand-dark hover:text-brand-orange transition-colors"
                                    data-testid="contact-phone-link"
                                >
                                    <Phone className="w-5 h-5" />
                                    <span className="font-serif italic text-xl">
                                        323-212-5323
                                    </span>
                                </a>
                                <div className="flex items-center gap-3 text-brand-muted">
                                    <MapPin className="w-5 h-5" />
                                    <span className="text-sm uppercase tracking-[0.2em]">
                                        Available worldwide · remote first
                                    </span>
                                </div>
                            </div>

                            {/* Newsletter */}
                            <div className="border-t border-brand-border-light pt-10">
                                <h3 className="font-serif text-2xl text-brand-dark mb-3">
                                    Monthly playbook in your inbox.
                                </h3>
                                <p className="text-sm text-brand-muted mb-5">
                                    One actionable F&B revenue idea per month. No spam, ever.
                                </p>
                                <form
                                    onSubmit={submitNewsletter}
                                    data-testid="newsletter-form"
                                    className="flex items-center gap-3 border-b border-brand-dark"
                                >
                                    <input
                                        type="email"
                                        required
                                        value={newsletterEmail}
                                        onChange={(e) => setNewsletterEmail(e.target.value)}
                                        placeholder="your@email.com"
                                        data-testid="newsletter-email-input"
                                        className="flex-1 bg-transparent py-3 text-base placeholder-brand-muted focus:outline-none"
                                    />
                                    <button
                                        type="submit"
                                        disabled={newsLoading}
                                        data-testid="newsletter-submit-button"
                                        className="text-brand-dark hover:text-brand-orange transition-colors disabled:opacity-50"
                                    >
                                        <ArrowRight className="w-5 h-5" />
                                    </button>
                                </form>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: form */}
                    <motion.form
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        onSubmit={submitContact}
                        data-testid="contact-form"
                        className="col-span-12 lg:col-span-7 space-y-8 lg:pl-10 lg:border-l border-brand-border-light"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.3em] text-brand-muted block mb-2">
                                    Name *
                                </label>
                                <input
                                    name="name"
                                    required
                                    value={form.name}
                                    onChange={handleChange}
                                    data-testid="contact-name-input"
                                    className={inputClass}
                                    placeholder="Your full name"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.3em] text-brand-muted block mb-2">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    value={form.email}
                                    onChange={handleChange}
                                    data-testid="contact-email-input"
                                    className={inputClass}
                                    placeholder="you@business.com"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.3em] text-brand-muted block mb-2">
                                    Business Name
                                </label>
                                <input
                                    name="business_name"
                                    value={form.business_name}
                                    onChange={handleChange}
                                    data-testid="contact-business-input"
                                    className={inputClass}
                                    placeholder="Your restaurant or brand"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.3em] text-brand-muted block mb-2">
                                    Package Interest
                                </label>
                                <select
                                    name="package_interest"
                                    value={form.package_interest}
                                    onChange={handleChange}
                                    data-testid="contact-package-select"
                                    className={inputClass + " appearance-none cursor-pointer"}
                                >
                                    <option value="">Select an option…</option>
                                    {packageOptions.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] uppercase tracking-[0.3em] text-brand-muted block mb-2">
                                Tell me about your project *
                            </label>
                            <textarea
                                name="message"
                                required
                                rows={5}
                                value={form.message}
                                onChange={handleChange}
                                data-testid="contact-message-input"
                                className={inputClass + " resize-none"}
                                placeholder="What are you working on? What's the goal?"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-button"
                            className="inline-flex items-center gap-3 bg-brand-orange text-brand-cream px-10 py-4 text-xs uppercase tracking-[0.25em] hover:bg-brand-orange-hover transition-colors disabled:opacity-50 group"
                        >
                            {submitting ? "Sending…" : "Send Message"}
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </button>
                    </motion.form>
                </div>
            </div>
        </section>
    );
}
